/**
 * Transactions List / Table Component
 * Displays transactions in a table with pagination
 */

import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TablePagination,
  IconButton, Tooltip, useTheme, alpha
} from '@mui/material';
import {
  PictureAsPdf as PictureAsPdfIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { supabase } from '../../../../supabaseClient';
import {
  TRANSACTION_TYPE_MAP,
  formatTransactionDate,
  formatCurrency
} from './transactionConstants';

const headerCellSx = (theme) => ({
  fontWeight: 500,
  fontSize: '0.9rem',
  bgcolor: 'white',
  borderBottom: `2px solid ${theme.palette.primary.main}`,
  color: 'primary.main',
  py: 2,
  px: 2,
  whiteSpace: 'nowrap'
});

const bodyCellSx = (theme) => ({
  py: 2.5,
  px: 2,
  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
});

async function viewFile(bucket, filePath) {
  if (!filePath) return;
  try {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(filePath, 60);
    if (error) throw error;
    window.open(data.signedUrl, '_blank');
  } catch (err) {
    console.error('Error viewing file:', err);
  }
}

async function downloadFile(bucket, filePath, fileName) {
  if (!filePath) return;
  try {
    const { data, error } = await supabase.storage.from(bucket).download(filePath);
    if (error) throw error;
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || filePath.split('/').pop();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Error downloading file:', err);
  }
}

function FileCell({ filePath, fileName, viewBucket, downloadBucket, viewTooltip, downloadTooltip, theme }) {
  if (!filePath) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
        No file
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
      <Tooltip title={viewTooltip || 'View'}>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); viewFile(viewBucket, filePath); }}
          sx={{
            bgcolor: alpha(theme.palette.error.main, 0.1),
            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
          }}
        >
          <PictureAsPdfIcon sx={{ color: 'error.main', fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title={downloadTooltip || 'Download'}>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); downloadFile(downloadBucket || viewBucket, filePath, fileName); }}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
          }}
        >
          <DownloadIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default function TransactionsList({
  transactions,
  totalCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  config,
  timeFormat,
  onViewTransaction
}) {
  const theme = useTheme();

  const getItemName = (tx) => tx[config.tableName]?.name || 'Unknown';
  const getItemSku  = (tx) => tx[config.tableName]?.sku  || '';

  const getLocationDisplay = (tx) => {
    if (tx.transaction_type === 'transfer') {
      const from = tx.from_location?.name || 'Unknown';
      const to   = tx.to_location?.name   || 'Unknown';
      return `${from} → ${to}`;
    }
    return tx.location?.name || '-';
  };

  const getQuantityDisplay = (tx) => {
    const qty = tx.quantity;
    if (qty === null || qty === undefined) return '-';
    const num = parseFloat(qty);
    const isNeg = num < 0;
    const color = isNeg ? 'error.main' : 'success.main';
    const sign = isNeg ? '' : '+';

    if (config?.hasUnitOfMeasure) {
      const item = tx[config.tableName];
      const unitType = item?.primary_unit_type;
      const unitMeasure = item?.primary_unit_measure;
      const totalQty = tx.total_quantity != null ? parseFloat(tx.total_quantity) : null;
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem', color }}>
            {sign}{num.toLocaleString()}{unitType ? ` ${unitType}` : ''}
          </Typography>
          {totalQty != null && unitMeasure && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              ({isNeg ? '' : '+'}{totalQty.toLocaleString()} {unitMeasure})
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem', color }}>
        {sign}{num.toLocaleString()}
      </Typography>
    );
  };

  const getDeliveryDisplay = (tx) => {
    if (tx.transaction_type !== 'purchase' || !tx.delivery_status) return '-';
    const colorMap = { ordered: 'info', shipped: 'warning', delivered: 'success', cancelled: 'error' };
    return (
      <Chip
        label={tx.delivery_status.charAt(0).toUpperCase() + tx.delivery_status.slice(1)}
        size="small"
        color={colorMap[tx.delivery_status] || 'default'}
        sx={{ fontWeight: 500, fontSize: '0.8rem' }}
      />
    );
  };

  const getTypeChip = (tx) => {
    const typeInfo = TRANSACTION_TYPE_MAP[tx.transaction_type];
    if (!typeInfo) return <Chip label={tx.transaction_type || 'Unknown'} size="small" />;
    return (
      <Chip
        icon={typeInfo.icon}
        label={typeInfo.label}
        size="small"
        color={typeInfo.color}
        sx={{
          fontWeight: 500,
          fontSize: '0.8rem',
          height: 28,
          border: '2px solid',
          borderColor: `${theme.palette[typeInfo.color]?.main || theme.palette.primary.main}`
        }}
      />
    );
  };

  const poBucket      = config?.storageBuckets?.po;
  const invoiceBucket = config?.storageBuckets?.invoices;

  return (
    <Paper sx={{
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
      overflow: 'hidden',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
      }
    }}>
      <TableContainer sx={{ overflowX: 'hidden' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx(theme), width: '14%' }}>Date</TableCell>
              <TableCell sx={{ ...headerCellSx(theme), width: '12%' }}>Type</TableCell>
              <TableCell sx={{ ...headerCellSx(theme), width: '17%' }}>Item</TableCell>
              <TableCell sx={{ ...headerCellSx(theme), width: '17%' }}>Location</TableCell>
              <TableCell sx={{ ...headerCellSx(theme), width: '9%' }}>Qty</TableCell>
              <TableCell sx={{ ...headerCellSx(theme), width: '9%' }}>Cost</TableCell>
              <TableCell sx={{ ...headerCellSx(theme), width: '10%' }}>Delivery</TableCell>
              <TableCell sx={{ ...headerCellSx(theme), width: '6%' }} align="center">PO</TableCell>
              <TableCell sx={{ ...headerCellSx(theme), width: '6%' }} align="center">Invoice</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((tx, index) => (
              <TableRow
                key={tx.id}
                hover
                onClick={() => onViewTransaction(tx)}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  bgcolor: index % 2 === 0 ? 'white' : alpha(theme.palette.grey[50], 0.8),
                  '&:hover': {
                    bgcolor: 'grey.50',
                    transform: 'translateX(4px)',
                    boxShadow: 'inset 4px 0 0 black'
                  },
                  '& .MuiTableCell-root': {
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                    py: 2.5,
                    px: 2
                  }
                }}
              >
                <TableCell sx={bodyCellSx(theme)}>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.primary' }}>
                    {formatTransactionDate(tx.transaction_date || tx.created_at, timeFormat)}
                  </Typography>
                </TableCell>
                <TableCell sx={bodyCellSx(theme)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    {getTypeChip(tx)}
                    {tx.po_number && (
                      <Chip label={`PO #${tx.po_number}`} size="small" variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem', borderColor: 'primary.main', color: 'primary.main' }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={bodyCellSx(theme)}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem', color: 'text.primary' }}>
                      {getItemName(tx)}
                    </Typography>
                    {getItemSku(tx) && (
                      <Typography variant="caption" color="text.secondary">
                        SKU: {getItemSku(tx)}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={bodyCellSx(theme)}>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                    {getLocationDisplay(tx)}
                  </Typography>
                </TableCell>
                <TableCell sx={bodyCellSx(theme)}>{getQuantityDisplay(tx)}</TableCell>
                <TableCell sx={bodyCellSx(theme)}>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.primary' }}>
                    {formatCurrency(tx.unit_cost)}
                  </Typography>
                </TableCell>
                <TableCell sx={bodyCellSx(theme)}>{getDeliveryDisplay(tx)}</TableCell>
                <TableCell sx={{ ...bodyCellSx(theme), textAlign: 'center' }}>
                  <FileCell filePath={tx.po_file_path} fileName={tx.po_file_name}
                    viewBucket={poBucket} downloadBucket={poBucket}
                    viewTooltip="View PO" downloadTooltip="Download PO" theme={theme} />
                </TableCell>
                <TableCell sx={{ ...bodyCellSx(theme), textAlign: 'center' }}>
                  <FileCell filePath={tx.invoice_file_path} fileName={tx.invoice_file_name}
                    viewBucket={invoiceBucket} downloadBucket={invoiceBucket}
                    viewTooltip="View Invoice" downloadTooltip="Download Invoice" theme={theme} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 6, px: 3 }}>
        <Paper sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          overflow: 'hidden'
        }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[12, 24, 48]}
          />
        </Paper>
      </Box>
    </Paper>
  );
}
